import { type ChangeEvent, useEffect, useId, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import {
  ChevronRightIcon,
  FormCard,
  PrimaryButton,
} from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import { formatClaimPolicySummaryLine, formatIncidentRailSummary } from '../claims/claimFlowModel'
import { persistWindscreenRepair, readClaimDraft } from '../claims/claimDraftStorage'
import { TRUSTED_WINDSCREEN_REPAIRER_COMPANY } from '../claims/windscreenRepairerConstants'

export type WindscreenRepairChoice = 'trusted' | 'own'

function buildWindscreenSearch(policyRef: string | null, typeInQuery: string | null): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (typeInQuery) p.set('type', typeInQuery)
  const q = p.toString()
  return q ? `?${q}` : ''
}

function repairCheckbox(checked: boolean) {
  return (
    <span
      className={`flex size-6 shrink-0 items-center justify-center rounded border-2 ${
        checked
          ? 'border-background-brand-solid bg-background-brand-solid'
          : 'border-border-primary bg-background-primary'
      }`}
      aria-hidden
    >
      {checked ? (
        <svg
          className="size-3.5 text-text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={3}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      ) : null}
    </span>
  )
}

/**
 * Windscreen branch: trusted repair network vs own repairer (+ optional own-repairer company name).
 */
export default function ClaimWindscreenRepairOptionStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const companyFieldId = useId()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  const [option, setOption] = useState<WindscreenRepairChoice>(() => {
    const o = readClaimDraft()?.windscreenRepairOption
    if (o === 'trusted' || o === 'own') return o
    return 'trusted'
  })
  const [company, setCompany] = useState(() => {
    const d = readClaimDraft()
    if (d?.windscreenRepairOption === 'own') return d.windscreenRepairerCompany ?? ''
    return d?.windscreenRepairerCompany?.trim() || TRUSTED_WINDSCREEN_REPAIRER_COMPANY
  })

  const windscreenSearch = buildWindscreenSearch(policyRef, typeInQuery)

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const d = readClaimDraft()
    if (d?.windscreenReplacedAlready === true) {
      navigate(`/claims/windscreen/review${windscreenSearch}`, {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    }
  }, [navigate, windscreenSearch, navState?.policy])

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const draft = readClaimDraft()
  const chipBool = draft?.windscreenSmallChip

  const persistCurrent = () => {
    persistWindscreenRepair(policyRef, {
      option,
      repairerCompany: option === 'trusted' ? TRUSTED_WINDSCREEN_REPAIRER_COMPANY : company,
      repairerEmail: '',
      repairerPhone: '',
    })
  }

  const backToDetail = () => {
    persistCurrent()
    navigate(`/claims/windscreen/detail${windscreenSearch}`, {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  const handleNext = () => {
    persistCurrent()
    const p = new URLSearchParams()
    if (policyRef) p.set('policyRef', policyRef)
    if (typeInQuery) p.set('type', typeInQuery)
    const search = p.toString()
    const path = search
      ? `/claims/windscreen/review?${search}`
      : '/claims/windscreen/review'
    navigate(path, {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  const selectTrusted = () => {
    setOption('trusted')
    setCompany(TRUSTED_WINDSCREEN_REPAIRER_COMPANY)
    persistWindscreenRepair(policyRef, {
      option: 'trusted',
      repairerCompany: TRUSTED_WINDSCREEN_REPAIRER_COMPANY,
      repairerEmail: '',
      repairerPhone: '',
    })
  }

  const selectOwn = () => {
    const nextCompany = company.trim() === TRUSTED_WINDSCREEN_REPAIRER_COMPANY ? '' : company
    setOption('own')
    setCompany(nextCompany)
    persistWindscreenRepair(policyRef, {
      option: 'own',
      repairerCompany: nextCompany,
      repairerEmail: '',
      repairerPhone: '',
    })
  }

  const onCompanyChange = (e: ChangeEvent<HTMLInputElement>) => setCompany(e.target.value)

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={backToDetail}
        title="Choose your repair option"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="windscreen"
            incidentSummary={formatIncidentRailSummary(draft)}
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
            windscreenDescription={draft?.windscreenDescription ?? ''}
            windscreenSmallChip={chipBool ?? null}
            windscreenReplacedAlready={draft?.windscreenReplacedAlready ?? null}
            windscreenRepairOption={option}
          />
        }
      >
        <FormCard>
          <div className="flex w-full flex-col items-stretch gap-[20px]">
            <fieldset className="m-0 w-full min-w-0 border-0 p-0">
              <legend className="sr-only">Choose your repair option</legend>
              <div className="flex flex-col gap-[12px]">
                <button
                  type="button"
                  role="radio"
                  aria-checked={option === 'trusted'}
                  onClick={selectTrusted}
                  className={`m-0 flex w-full cursor-pointer flex-col gap-[12px] rounded-2xl border-2 bg-background-primary p-[16px] text-left shadow-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2 ${
                    option === 'trusted'
                      ? 'border-background-brand-solid bg-background-secondary'
                      : 'border-border-primary'
                  }`}
                >
                  <div className="flex w-full items-start justify-between gap-[12px]">
                    <div className="min-w-0 flex-1">
                      <p className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">
                        Club 4X4&apos;s trusted repairer
                      </p>
                      <p className="mt-[8px] m-0 font-body text-sm leading-sm text-text-secondary-700">
                        For a fast and streamlined process book your repair with our trusted partners at
                        National Windscreen Group (NWG).
                      </p>
                    </div>
                    {repairCheckbox(option === 'trusted')}
                  </div>
                </button>

                <button
                  type="button"
                  role="radio"
                  aria-checked={option === 'own'}
                  onClick={selectOwn}
                  className={`m-0 flex w-full cursor-pointer flex-col gap-[12px] rounded-2xl border-2 bg-background-primary p-[16px] text-left shadow-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2 ${
                    option === 'own'
                      ? 'border-background-brand-solid bg-background-secondary'
                      : 'border-border-primary'
                  }`}
                >
                  <div className="flex w-full items-start justify-between gap-[12px]">
                    <div className="min-w-0 flex-1">
                      <p className="m-0 font-body font-semibold text-md leading-md text-text-primary-900">
                        Choose your own repairer
                      </p>
                      <p className="mt-[8px] m-0 font-body text-sm leading-sm text-text-secondary-700">
                        Pick any repairer you want, pay them for the work, then send us the invoice.
                        We&apos;ll reimburse you.
                      </p>
                    </div>
                    {repairCheckbox(option === 'own')}
                  </div>
                </button>
              </div>
            </fieldset>

            {option === 'trusted' ? (
              <div className="flex w-full flex-col gap-[16px] border-t border-border-secondary pt-[20px]">
                <div>
                  <h3 className="m-0 font-display font-semibold text-md leading-md text-text-primary-900">
                    Preferred repairer
                  </h3>
                  <p className="mt-[4px] m-0 font-body text-sm leading-sm text-text-tertiary-600">
                    Your repair will be arranged through our trusted partner NWG.
                  </p>
                </div>

                <div className="flex flex-col gap-[8px]">
                  <label htmlFor={companyFieldId} className="font-body text-sm font-semibold text-text-primary-900">
                    Company name
                  </label>
                  <input
                    id={companyFieldId}
                    type="text"
                    value={TRUSTED_WINDSCREEN_REPAIRER_COMPANY}
                    readOnly
                    aria-readonly="true"
                    className="w-full rounded-xl border border-solid border-border-primary bg-background-secondary px-[14px] py-[12px] font-body text-sm leading-sm text-text-primary-900 shadow-xs focus:outline-none"
                  />
                </div>
              </div>
            ) : null}

            {option === 'own' ? (
              <div className="flex w-full flex-col gap-[16px] border-t border-border-secondary pt-[20px]">
                <div>
                  <h3 className="m-0 font-display font-semibold text-md leading-md text-text-primary-900">
                    Repairer details
                  </h3>
                  <p className="mt-[4px] m-0 font-body text-sm leading-sm text-text-tertiary-600">
                    Optional — add the repairer name if you know it.
                  </p>
                </div>

                <div className="flex flex-col gap-[8px]">
                  <label htmlFor={companyFieldId} className="font-body text-sm font-semibold text-text-primary-900">
                    Company name
                  </label>
                  <input
                    id={companyFieldId}
                    type="text"
                    value={company}
                    onChange={onCompanyChange}
                    autoComplete="organization"
                    className="w-full rounded-xl border border-solid border-border-primary bg-background-primary px-[14px] py-[12px] font-body text-sm leading-sm text-text-primary-900 shadow-xs placeholder:text-text-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2"
                  />
                </div>
              </div>
            ) : null}
          </div>
        </FormCard>

        <div className="sticky bottom-0 w-full border-t border-border-secondary bg-background-secondary pt-[12px] pb-[8px] md:static md:border-t-0 md:py-0">
          <PrimaryButton
            type="button"
            size="lg"
            fullWidth
            onClick={handleNext}
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label="Next"
          >
            Next
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
